import {
  ComposedInvoiceStatus,
  PartnerInvoiceFundingStatus,
  PartnerInvoiceStatus,
  PartnerInvoiceType,
} from 'src/constants/invoice/enums'
import type {
  ComposedInvoice,
  PartnerInvoice,
  PartnerInvoiceStatusCounts,
} from 'src/interfaces/invoice'

/** The two funding statuses that mean an admin has to intervene. */
const FAILED_FUNDING_STATUSES = [
  PartnerInvoiceFundingStatus.Failed,
  PartnerInvoiceFundingStatus.Error,
]

/** Fixed display order, so a mixed composed invoice always reads "Loan, Wire". */
const TYPE_DISPLAY_ORDER = [
  PartnerInvoiceType.Loan,
  PartnerInvoiceType.Wire,
  PartnerInvoiceType.Historical,
]

export const isPartnerInvoiceFundingFailed = (invoice: PartnerInvoice): boolean => {
  if (invoice.loanFundingStatus === undefined) {
    return false
  }

  return FAILED_FUNDING_STATUSES.includes(invoice.loanFundingStatus)
}

export const countPartnerInvoicesByStatus = (
  invoice: ComposedInvoice,
): PartnerInvoiceStatusCounts => {
  return invoice.partnerInvoices.reduce<PartnerInvoiceStatusCounts>(
    (accumulator, partnerInvoice) => {
      if (partnerInvoice.status === PartnerInvoiceStatus.Canceled) {
        accumulator.canceled += 1

        // Canceled rows are excluded from `total` — `sumPartnerInvoiceAmounts` already excludes
        // them from the Amount total, and a "2 of 6 paid" that can never reach 6 of 6 because
        // two of the six are canceled is a denominator that misreports progress.
        return accumulator
      }

      accumulator.total += 1

      // "Funded" here means funding has landed, which repaid invoices have also passed
      // through — the chip's "2 of 6 funded" must not go backwards once repayment starts.
      if (
        partnerInvoice.status === PartnerInvoiceStatus.Funded ||
        partnerInvoice.status === PartnerInvoiceStatus.RepaymentPending ||
        partnerInvoice.status === PartnerInvoiceStatus.Repaid
      ) {
        accumulator.funded += 1
      }

      if (partnerInvoice.status === PartnerInvoiceStatus.Repaid) {
        accumulator.paid += 1
      }

      if (isPartnerInvoiceFundingFailed(partnerInvoice)) {
        accumulator.fundingFailed += 1
      }

      return accumulator
    },
    { total: 0, funded: 0, paid: 0, canceled: 0, fundingFailed: 0 },
  )
}

/**
 * The composed-invoice DTO has no status, so the chip is derived from the nested rows. First
 * match wins, and the order is deliberate:
 *
 * 1. Canceled  — a fully cancelled composed invoice is done, whatever its funding history says.
 * 2. FundingFailed — the state that needs an admin, so it outranks the progress states even
 *    when some rows have already moved.
 * 3. FundedAndPaid — every non-canceled row repaid is the terminal success.
 * 4. Repaying — any repayment started but not finished.
 * 5. AwaitingFunding — the residual, and the answer for an empty list.
 */
export const deriveComposedInvoiceStatus = (invoice: ComposedInvoice): ComposedInvoiceStatus => {
  const { partnerInvoices } = invoice

  if (partnerInvoices.length === 0) {
    return ComposedInvoiceStatus.AwaitingFunding
  }

  const allCanceled = partnerInvoices.every((partnerInvoice) => {
    return partnerInvoice.status === PartnerInvoiceStatus.Canceled
  })

  if (allCanceled) {
    return ComposedInvoiceStatus.Canceled
  }

  const anyFundingFailed = partnerInvoices.some((partnerInvoice) => {
    return isPartnerInvoiceFundingFailed(partnerInvoice)
  })

  if (anyFundingFailed) {
    return ComposedInvoiceStatus.FundingFailed
  }

  const activeInvoices = partnerInvoices.filter((partnerInvoice) => {
    return partnerInvoice.status !== PartnerInvoiceStatus.Canceled
  })

  const allRepaid = activeInvoices.every((partnerInvoice) => {
    return partnerInvoice.status === PartnerInvoiceStatus.Repaid
  })

  if (allRepaid) {
    return ComposedInvoiceStatus.FundedAndPaid
  }

  const anyRepaying = activeInvoices.some((partnerInvoice) => {
    return (
      partnerInvoice.status === PartnerInvoiceStatus.Repaid ||
      partnerInvoice.status === PartnerInvoiceStatus.RepaymentPending
    )
  })

  if (anyRepaying) {
    return ComposedInvoiceStatus.Repaying
  }

  return ComposedInvoiceStatus.AwaitingFunding
}

/** Cents. Canceled invoices are excluded — their money is not owed. */
export const sumPartnerInvoiceAmounts = (invoice: ComposedInvoice): number => {
  return invoice.partnerInvoices.reduce<number>((accumulator, partnerInvoice) => {
    if (partnerInvoice.status === PartnerInvoiceStatus.Canceled) {
      return accumulator
    }

    return accumulator + partnerInvoice.amount
  }, 0)
}

/**
 * Distinct nested types in a fixed order. Builds its own array rather than sorting
 * `partnerInvoices` — that array is Redux state and must never be sorted in place.
 */
export const deriveComposedInvoiceTypes = (invoice: ComposedInvoice): PartnerInvoiceType[] => {
  return TYPE_DISPLAY_ORDER.filter((type) => {
    return invoice.partnerInvoices.some((partnerInvoice) => {
      return partnerInvoice.type === type
    })
  })
}

/**
 * Whether the invoice document exists. There is no admin-side download endpoint (the only
 * download route is customer-authenticated), so this stays a yes/no indicator, not a link.
 */
export const isComposedInvoiceGenerated = (invoice: ComposedInvoice): boolean => {
  if (invoice.gcsPath !== null && invoice.gcsPath !== undefined && invoice.gcsPath !== '') {
    return true
  }

  return invoice.invoiceId !== null && invoice.invoiceId !== undefined && invoice.invoiceId !== ''
}
